import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

const integrationLinks = [
  { title: 'Integrations explained', href: '#integrations-explained' },
  { title: 'How to connect and manage your integrations', href: '#manage-integrations' },
];

const integrationCategories = [
  { title: 'Google Workspace integration', href: '#google-workspace' },
  { title: 'Microsoft 365 (Outlook) integration', href: '#microsoft-365' },
  { title: 'Slack integration', href: '#slack' },
  { title: 'Microsoft Teams integration', href: '#teams' },
  { title: 'Discord integration', href: '#discord' },
  { title: 'Telegram integration', href: '#telegram' },
  { title: 'Notion integration', href: '#notion' },
  { title: 'Trello integration', href: '#trello' },
  { title: 'Asana integration', href: '#asana' },
  { title: 'Salesforce integration', href: '#salesforce' },
  { title: 'HubSpot integration', href: '#hubspot' },
  { title: 'Pipedrive integration', href: '#pipedrive' },
  { title: 'Zoho CRM integration', href: '#zoho' },
  { title: 'Mailchimp integration', href: '#mailchimp' },
  { title: 'Buffer integration', href: '#buffer' },
  { title: 'Hootsuite integration', href: '#hootsuite' },
  { title: 'Shopify integration', href: '#shopify' },
  { title: 'WooCommerce integration', href: '#woocommerce' },
  { title: 'Stripe integration', href: '#stripe' },
  { title: 'PayPal integration', href: '#paypal' },
  { title: 'Zapier integration', href: '#zapier' },
  { title: 'Make (Integromat) integration', href: '#make' },
  { title: 'Webhooks integration', href: '#webhooks' },
];

const faqLinks = [
  { title: 'Integration FAQ', href: '#integration-faq' },
  { title: 'Troubleshooting integration issues', href: '#troubleshooting' },
];

interface LinkItemProps {
  title: string;
  href: string;
  isDark: boolean;
}

function LinkItem({ title, href, isDark }: LinkItemProps) {
  return (
    <Link
      to={href}
      className={`flex items-center justify-between py-4 px-2 transition-all group border-b ${
        isDark
          ? 'border-white/5 hover:bg-white/5'
          : 'border-slate-100 hover:bg-slate-50'
      }`}
    >
      <span className={`font-medium transition-colors ${
        isDark ? 'text-white group-hover:text-purple-300' : 'text-slate-900 group-hover:text-purple-600'
      }`}>
        {title}
      </span>
      <ChevronRight className={`w-5 h-5 transition-colors ${
        isDark ? 'text-gray-600 group-hover:text-purple-400' : 'text-slate-300 group-hover:text-purple-600'
      }`} />
    </Link>
  );
}

interface SectionCardProps {
  title: string;
  links: Array<{ title: string; href: string }>;
  isDark: boolean;
  delay?: number;
}

function SectionCard({ title, links, isDark, delay = 0 }: SectionCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className={`rounded-2xl border overflow-hidden ${
        isDark
          ? 'bg-dark-800/50 border-white/5'
          : 'bg-white border-slate-200 shadow-sm'
      }`}
    >
      <div className={`px-6 py-5 border-b ${isDark ? 'border-white/5' : 'border-slate-100'}`}>
        <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{title}</h2>
      </div>
      <div className="px-4">
        {links.map((link) => (
          <LinkItem
            key={link.title + link.href}
            title={link.title}
            href={link.href}
            isDark={isDark}
          />
        ))}
      </div>
    </motion.div>
  );
}

export default function IntegrationsDocs() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <StaticPageLayout
      title=""
      subtitle=""
      showBackButton={false}
    >
      {/* Breadcrumbs */}
      <nav className="mb-8">
        <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
          <li>
            <Link
              to="/docs"
              className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
            >
              All Collections
            </Link>
          </li>
          <li>
            <ChevronRight className="w-4 h-4" />
          </li>
          <li className={isDark ? 'text-white' : 'text-slate-900'}>
            Integrations
          </li>
        </ol>
      </nav>

      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-10"
      >
        <h1 className={`text-4xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Integrations
        </h1>
        <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          Connect SweetPilot with your favorite tools and services.
        </p>
      </motion.div>

      {/* Getting Started with Integrations */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className={`rounded-2xl border overflow-hidden mb-6 ${
          isDark
            ? 'bg-dark-800/50 border-white/5'
            : 'bg-white border-slate-200 shadow-sm'
        }`}
      >
        <div className="px-4">
          {integrationLinks.map((link) => (
            <LinkItem
              key={link.title}
              title={link.title}
              href={link.href}
              isDark={isDark}
            />
          ))}
        </div>
      </motion.div>

      {/* All Integrations */}
      <SectionCard
        title="Available Integrations"
        links={integrationCategories}
        isDark={isDark}
        delay={0.2}
      />

      <div className="h-6" />

      {/* FAQ & Troubleshooting */}
      <SectionCard
        title="Help & Support"
        links={faqLinks}
        isDark={isDark}
        delay={0.3}
      />

      <div className="h-16" />

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-center"
      >
        <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
          isDark ? 'bg-purple-500/20' : 'bg-purple-100'
        }`}>
          <span className="text-2xl">🔌</span>
        </div>
        <a
          href="mailto:help@sweetpilot.com"
          className={`text-sm transition-colors ${
            isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
          }`}
        >
          help@sweetpilot.com
        </a>
      </motion.div>
    </StaticPageLayout>
  );
}
